import { deepSignal } from 'deepsignal';
import { CHARITY } from '@util/const.js';
import { useState } from 'preact/hooks';
import { isYes } from '@util/common.js';
import CustomizeFullProgressBar from './customize-full-progress-bar.jsx';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import Description from '@components/overlays/shared/description.jsx';
import Picker from '@components/input/picker.input.jsx';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import {
  BasicProgressBar,
  toBasicProgressBarTextColor,
  toBasicProgressBarTheme,
} from '@playlive/overlay-core/progress-bars/basic';
import { toColor } from '@playlive/overlay-core/colors';
import DemoAnimationsDropdown from '@components/overlays/shared/demo-animations-dropdown.jsx';
import usePreviewAnimations from '@hooks/usePreviewAnimations.js';
import useCommon from '@hooks/useCommon.js';
import useDemoProgressBarData from '@hooks/useDemoProgressBarData.js';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  textColor: 'light-aqua',
  totalValue: 750,
  goalValue: 1000,
  bgColor: 'default-red',
  showChallenges: 'yes',
  showRewards: 'yes',
  showDonors: 'yes',
  showScroller: 'yes',
  donationTimeOffset: 1,
  showAllDonations: 'no',
  type: 'classic',
});

export default function BasicProgressBarPage() {
  const [showMilestones, setMilestones] = useState(false);
  const [currentAmount, setCurrentAmount] = useState(750);
  const { overlayInfo, overlayURL, setConfigState } = useCommon({ config });

  const themes = [
    { name: 'Slanted', value: 'slanted' },
    { name: 'Classic', value: 'classic' },
  ];

  const { demoDonations, demoMilestones, demoTargets, demoRewards } =
    useDemoProgressBarData(config);

  const { isPreviewingAnimation, startAnimation } = usePreviewAnimations({
    setMilestones,
    setCurrentAmount,
  });

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col items-center justify-center  overflow-x-hidden">
        <div className="example_overlay relative w-full min-h-40">
          <ScaledOverlayWrapper
            allowScaling={false}
            defaultScale={'scale-100'}
            innerClassName="h-[220px]"
          >
            <BasicProgressBar
              className="shadow-2xl"
              goalAmount={1000}
              currentAmount={currentAmount}
              textColor={toBasicProgressBarTextColor(config.textColor)}
              backgroundColor={toColor(config.bgColor)}
              completed={false}
              charityType={config.charityType}
              milestones={demoMilestones}
              donations={demoDonations}
              rewards={demoRewards}
              showChallenges={isYes(config.showChallenges)}
              showRewards={isYes(config.showRewards)}
              showDonors={isYes(config.showDonors)}
              showScroller={isYes(config.showScroller)}
              demoShowMilestones={showMilestones}
              targets={demoTargets}
              type={toBasicProgressBarTheme(config.type)}
            />
          </ScaledOverlayWrapper>

          <DemoAnimationsDropdown
            onChosen={startAnimation}
            disabled={isPreviewingAnimation}
          />
        </div>

        <Description>
          <p className="pb-4">
            Utilize our dynamic Classic overlay to enhance your Tiltify
            fundraising campaign. Track your progress, recognize donors and
            promote upcoming milestones and incentives.
          </p>
        </Description>

        <CustomizeFullProgressBar
          config={config}
          setConfigState={setConfigState}
          overlayURL={overlayURL}
          setMilestones={setMilestones}
          overlayName={overlayInfo.name}
          obsDimensions={overlayInfo.obsDimensions}
          setCurrentAmount={setCurrentAmount}
          childrenSlot="middle"
        >
          <CustomizeSection title="Theme">
            <Picker
              onChange={setConfigState}
              currentValue={config.type}
              items={themes}
              fieldName="type"
            />
          </CustomizeSection>
        </CustomizeFullProgressBar>
      </div>
    </div>
  );
}
